/* Alegerea valorii pe pagina de voucher cadou (sections/armonia-voucher.liquid).
 *
 * Shopify nu are „scrie tu suma" pe planurile non-Plus: valoarea unui card cadou ESTE prețul
 * variantei. Produsul are deci o scară de trepte (100–10.000 lei, din 50 în 50), iar rolul
 * acestui fișier e să facă scara să se simtă ca un câmp liber.
 *
 * Un singur câmp `name="id"` există în pagină — select-ul cu toate treptele. Fără JavaScript
 * el rămâne vizibil și complet funcțional. Cu JavaScript se ascunde, iar butoanele de alegere
 * rapidă și câmpul de sumă nu fac altceva decât să-i schimbe valoarea. Nu există a doua sursă
 * de adevăr, deci nu există stare de sincronizat.
 *
 * Treapta, minimul și maximul NU sunt scrise aici: se citesc din opțiunile randate de Liquid.
 * Dacă scara se schimbă din `migration/build_voucher_graphql.py`, interfața o urmează singură.
 *
 * Etichetele de sumă vin tot din `<option>` (`money_without_trailing_zeros` din Liquid), ca să
 * nu duplicăm formatarea de bani din armonia-produs.js.
 */

(() => {
  'use strict';

  class VoucherArmonia {
    constructor(radacina) {
      this.el = radacina;
      this.select = radacina.querySelector('[data-select]');
      this.grila = radacina.querySelector('[data-rapide]');
      this.custom = radacina.querySelector('[data-custom]');
      this.camp = radacina.querySelector('[data-suma]');
      this.nota = radacina.querySelector('[data-nota]');
      this.butonAlta = radacina.querySelector('[data-alta]');
      this.butoane = Array.from(radacina.querySelectorAll('[data-treapta]'));

      if (!this.select || !this.grila) return;

      this.trepte = Array.from(this.select.options)
        .filter((o) => !o.disabled)
        .map((o) => ({ lei: parseInt(o.dataset.lei, 10), id: o.value, text: o.textContent.trim() }))
        .filter((t) => Number.isFinite(t.lei))
        .sort((a, b) => a.lei - b.lei);

      if (this.trepte.length < 2) return;

      this.min = this.trepte[0].lei;
      this.max = this.trepte[this.trepte.length - 1].lei;
      this.pas = this.trepte[1].lei - this.trepte[0].lei;

      this.porneste();
    }

    porneste() {
      // De aici încolo select-ul e un detaliu de implementare, nu o interfață.
      this.select.hidden = true;
      this.grila.hidden = false;
      this.el.classList.add('av-js');

      if (this.camp) {
        this.camp.min = this.min;
        this.camp.max = this.max;
        this.camp.step = this.pas;
        this.camp.placeholder = this.min;
      }

      this.limiteazaDataDeTrimitere();

      this.butoane.forEach((b) => {
        b.addEventListener('click', () => this.alege(b.dataset.treapta, { dinButon: true }));
      });

      if (this.butonAlta) {
        this.butonAlta.addEventListener('click', () => this.deschideCustom());
      }

      if (this.camp) {
        this.camp.addEventListener('input', () => this.dinCamp(false));
        this.camp.addEventListener('change', () => this.dinCamp(true));
      }

      this.actualizeazaButoane();
    }

    /* Shopify refuză o dată de trimitere la mai mult de 90 de zile — verificat pe magazin:
     * ziua 90 trece, ziua 91 întoarce 422 «Send on trebuie să fie în următoarele 90 de zile».
     * Fără marginile astea, cine cumpără în septembrie un cadou pentru Crăciun află limita
     * dintr-o eroare, după ce a completat tot formularul.
     *
     * Data se compune din componentele LOCALE, nu din `toISOString()`: acela trece prin UTC și
     * seara, în România, ar da ziua următoare. */
    limiteazaDataDeTrimitere() {
      const camp = this.el.querySelector('[name="properties[Send on]"]');
      if (!camp) return;
      const zi = (d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const azi = new Date();
      const ultima = new Date(azi);
      ultima.setDate(ultima.getDate() + 90);
      camp.min = zi(azi);
      camp.max = zi(ultima);
    }

    treaptaDupaId(id) {
      return this.trepte.find((t) => t.id === id) || null;
    }

    /* Cea mai apropiată treaptă de o sumă oarecare. Scanare, nu aritmetică pe pas: dacă scara
     * devine neuniformă, funcția rămâne corectă fără să fie rescrisă. */
    ceaMaiApropiata(lei) {
      return this.trepte.reduce((cea, t) =>
        Math.abs(t.lei - lei) < Math.abs(cea.lei - lei) ? t : cea
      );
    }

    alege(id, optiuni) {
      const t = this.treaptaDupaId(id);
      if (!t) return;
      this.select.value = t.id;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
      if (optiuni && optiuni.dinButon) {
        this.inchideCustom();
      }
      this.actualizeazaButoane();
    }

    deschideCustom() {
      if (!this.custom) return;
      this.custom.hidden = false;
      this.butonAlta.setAttribute('aria-expanded', 'true');
      this.butonAlta.setAttribute('aria-pressed', 'true');
      /* Câmpul pornește de la valoarea deja aleasă, ca să nu existe nicio clipă în care
       * butonul arată «altă valoare» iar coșul ar primi altceva. Conținutul se SELECTEAZĂ:
       * altfel prima cifră tastată se lipește la coada celei vechi (1.000 + 1777 = 10001777),
       * ceea ce pe telefon e exact ce pățește oricine. */
      const curenta = this.treaptaDupaId(this.select.value);
      if (this.camp) {
        if (curenta) this.camp.value = curenta.lei;
        this.camp.focus();
        this.camp.select();
      }
      this.actualizeazaButoane();
      this.dinCamp(false);
    }

    inchideCustom() {
      if (!this.custom) return;
      this.custom.hidden = true;
      this.butonAlta.setAttribute('aria-expanded', 'false');
      if (this.nota) this.nota.textContent = '';
    }

    /* `definitiv` = evenimentul `change` (câmpul a fost părăsit). Abia atunci scriem în câmp
     * suma rotunjită: dacă am face-o la fiecare tastă, cifrele s-ar rescrie sub degete. */
    dinCamp(definitiv) {
      if (!this.camp) return;
      const brut = parseInt(this.camp.value, 10);

      if (!Number.isFinite(brut)) {
        if (this.nota) this.nota.textContent = '';
        return;
      }

      const t = this.ceaMaiApropiata(brut);
      this.alege(t.id);

      if (definitiv) this.camp.value = t.lei;

      if (this.nota) {
        if (brut < this.min) {
          this.nota.textContent = `Valoarea minimă e ${this.trepte[0].text}.`;
        } else if (brut > this.max) {
          this.nota.textContent =
            `Peste ${this.trepte[this.trepte.length - 1].text} emitem voucherul manual — scrie-ne.`;
        } else if (brut !== t.lei) {
          this.nota.textContent = `Rotunjit la ${t.text}: valorile merg din ${this.pas} în ${this.pas} lei.`;
        } else {
          this.nota.textContent = `Voucher de ${t.text}.`;
        }
      }
    }

    actualizeazaButoane() {
      const id = this.select.value;
      let potrivitDeButon = false;
      this.butoane.forEach((b) => {
        const activ = b.dataset.treapta === id;
        if (activ) potrivitDeButon = true;
        b.setAttribute('aria-pressed', activ ? 'true' : 'false');
      });
      if (this.butonAlta && this.custom && this.custom.hidden) {
        this.butonAlta.setAttribute('aria-pressed', potrivitDeButon ? 'false' : 'true');
      }
    }
  }

  function porneste(zona) {
    (zona || document).querySelectorAll('[data-armonia-voucher]').forEach((el) => {
      if (el.dataset.pornit) return;
      el.dataset.pornit = '1';
      new VoucherArmonia(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => porneste());
  } else {
    porneste();
  }
  // theme editor: secțiunea se re-randează fără reload
  document.addEventListener('shopify:section:load', (e) => porneste(e.target));
})();
